import type { FC } from "react";

import { Button } from "@jti/core-components/components/Button";
import LoyaltyLogo from "@jti/ui/icons/loyalty_logo.svg";

import { replacePointsPlaceholder } from "../types";

import styles from "./QuizCompleteScreen.module.scss";

export type QuizCtaConfig = {
  label: string;
  href?: string;
  onClick?: () => void;
};

export type QuizCompleteScreenProps = {
  title: string;
  description: string;
  points: number;
  pointsLabel: string;
  defaultCtaLabel: string;
  onDefaultCtaClick: () => void;
  ctas?: QuizCtaConfig[];
};

const RESPONSIVE_BUTTONS = [
  { size: "sm", className: styles.mobileButton },
  { size: "lg", className: styles.desktopButton },
] as const;

const QuizCta: FC<{ cta: QuizCtaConfig; variant: "primary" | "secondary" }> = ({ cta, variant }) => {
  const linkProps = cta.href ? { href: cta.href } : {};

  return (
    <>
      {RESPONSIVE_BUTTONS.map(({ size, className }) => (
        <Button key={size} variant={variant} size={size} className={className} onClick={cta.onClick} {...linkProps}>
          {cta.label}
        </Button>
      ))}
    </>
  );
};

export const QuizCompleteScreen: FC<QuizCompleteScreenProps> = ({
  title,
  description,
  points,
  pointsLabel,
  defaultCtaLabel,
  onDefaultCtaClick,
  ctas,
}) => {
  const earnedText = replacePointsPlaceholder(pointsLabel, points);

  const activeCtas: QuizCtaConfig[] =
    ctas && ctas.length > 0 ? ctas.slice(0, 2) : [{ label: defaultCtaLabel, onClick: onDefaultCtaClick }];

  const getVariant = (index: number): "primary" | "secondary" =>
    activeCtas.length > 1 && index === 0 ? "secondary" : "primary";

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>{title}</h2>

      <div className={styles.panel}>
        <p className={styles.description}>{description}</p>

        <div className={styles.badge} data-testid="quizCompleteBadge">
          <div className={styles.badgeLogoWrapper}>
            <LoyaltyLogo className={styles.badgeLogo} aria-hidden="true" />
          </div>
          <hr className={styles.badgeDivider} aria-hidden="true" />
          <p className={styles.badgeText}>{earnedText}</p>
        </div>
      </div>

      <div className={styles.ctas}>
        {activeCtas.map((cta, index) => (
          <QuizCta key={`${cta.label}-${index}`} cta={cta} variant={getVariant(index)} />
        ))}
      </div>
    </div>
  );
};
